// services/councilCost.ts — G4b overshoot wire domain (no rendering)
import type { OvershootEvent, BudgetOvershootWire } from '../types/council';

export function overshootKey(e: { turn_message_id: string; scope: string }): string {
  return `${e.turn_message_id}|${e.scope}`;
}

/** Accept a persisted entry OR a live wire target; fill v/detected_at. No decimal
 *  canonicalization — the backend serializes Decimal identically on both paths
 *  (verified: Pydantic model_dump(mode="json") == str(Decimal)). The live path passes
 *  NO liveDetectedAt → we stamp with the FE clock (Z-suffixed) so reconcileOnRefresh
 *  compares same-clock/same-format against sinceIso (the backend wire ts is naive no-Z). */
export function normalizeOvershoot(
  input: OvershootEvent | BudgetOvershootWire,
  liveDetectedAt?: string,
): OvershootEvent {
  const a = input as any;
  return {
    v: a.v ?? 1,
    turn_message_id: a.turn_message_id, scope: a.scope,
    scope_cap: a.scope_cap, used_after: a.used_after,
    estimate_usd: a.estimate_usd, actual_usd: a.actual_usd,
    overshoot_ratio: a.overshoot_ratio,
    detected_at: a.detected_at ?? liveDetectedAt ?? new Date().toISOString(),
  };
}

/** Live WS path: dedup-append by (turn_message_id, scope). Dup-drop is correct, NOT lossy:
 *  the backend is idempotent-by-turn (cost_window_updater.py:70 short-circuits — exactly one
 *  emit per turn), so a "corrected actual" for the same key never arrives. Do NOT change to
 *  last-write-wins (it would diverge from the server list and lose on the next reconcile). */
export function upsertOvershoot(list: OvershootEvent[] | undefined, entry: OvershootEvent): OvershootEvent[] {
  const cur = list ?? [];
  if (cur.some(e => overshootKey(e) === overshootKey(entry))) return cur;
  return [...cur, entry];
}

/** Refresh path: server list is authoritative; re-add ONLY live-only entries newer
 *  than the fetch start (genuine in-flight race). Older live-only entries are
 *  server-trimmed/G3-dismissed → evicted (no immortal breaches).
 *  TIMESTAMP FORMATS DIVERGE (persisted µs+Z, live Pydantic µs+no-Z, sinceIso ms+Z)
 *  → parse with Date.parse, NEVER lexicographic. NaN (unparseable) → fail-safe KEEP
 *  (never silently drop a live breach). The live path stamps detected_at with the
 *  FE clock (normalizeOvershoot with no backend ts) so both sides share clock+format. */
export function reconcileOnRefresh(
  serverList: OvershootEvent[] | undefined,
  priorList: OvershootEvent[] | undefined,
  sinceIso: string,
): OvershootEvent[] {
  const out = [...(serverList ?? [])];
  const serverKeys = new Set(out.map(overshootKey));
  const since = Date.parse(sinceIso);
  for (const e of priorList ?? []) {
    if (serverKeys.has(overshootKey(e))) continue;
    const t = Date.parse(e.detected_at);
    if (!Number.isFinite(since) || !Number.isFinite(t) || t > since) out.push(e);  // fail-safe keep
  }
  return out;
}

function _moreSevere(a: OvershootEvent, b: OvershootEvent): boolean {
  if (a.scope === 'session' && b.scope !== 'session') return true;
  if (b.scope === 'session' && a.scope !== 'session') return false;
  return parseFloat(a.overshoot_ratio) > parseFloat(b.overshoot_ratio);
}

/** Node breach membership by turn_message_id, with a deterministic representative
 *  (prefer session scope, else higher overshoot_ratio) for the hover. */
export function buildOvershootByTurn(events: OvershootEvent[] | undefined): Map<string, OvershootEvent> {
  const m = new Map<string, OvershootEvent>();
  for (const e of events ?? []) {
    const cur = m.get(e.turn_message_id);
    if (!cur || _moreSevere(e, cur)) m.set(e.turn_message_id, e);
  }
  return m;
}
